import { useState } from "react";

import { globalApi } from "@/lib/api";
import type { GeocodeResult } from "@/types";

/**
 * The ZIP code, and the place it resolves to.
 *
 * This is the only field on either setup screen that decides something the
 * owner cannot see: it is what "sunset" means here, which is what the overnight
 * check, the sundown timing on a chlorine dose and the daylight behind the test
 * reminder are all computed from. Until it is set, all three quietly do
 * nothing — which is precisely how a pool ends up with an app that says "no
 * location set" while the header reads "Crooks, SD".
 *
 * So the answer is looked up and **shown before it is saved**. A mistyped digit
 * resolves to a real town a long way away rather than to an error, and a
 * silently wrong sunset is the kind of quiet wrong answer this codebase spends
 * its comments guarding against. Naming it back — "Crooks, South Dakota" — is
 * the same show-the-working rule the dose cards follow.
 *
 * The lookup runs here and only here. The coordinates are then stored, so
 * nothing on the advice path ever depends on a third party being reachable —
 * see `api/chemistry/sun.py`, which makes that argument at length.
 */
export default function PostalCodeField({
  value,
  onChange,
  onResolved,
  resolved,
  hasCoordinates,
}: {
  value: string;
  onChange: (next: string) => void;
  /** Called with the confirmed place, for the parent to save. */
  onResolved: (place: GeocodeResult) => void;
  /** The place resolved this session, if any. */
  resolved: GeocodeResult | null;
  /** Whether the pool already has stored coordinates. */
  hasCoordinates: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function look() {
    setBusy(true);
    setError(null);
    try {
      onResolved(await globalApi.post<GeocodeResult>("/api/geocode", { postal_code: value }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not look that up.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <label htmlFor="postal-code" className="block text-sm font-medium">
        ZIP code
      </label>
      <p className="mt-0.5 text-xs text-(--color-ink-muted)">
        Sets sunrise and sunset here, which the overnight chlorine check and the "wait until
        after sundown" advice both depend on.
      </p>
      <div className="mt-1.5 flex gap-2">
        <input
          id="postal-code"
          className="field figure flex-1"
          inputMode="numeric"
          autoComplete="postal-code"
          placeholder="57020"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setError(null);
          }}
        />
        <button
          type="button"
          onClick={look}
          disabled={busy || value.trim() === ""}
          className="btn btn-quiet shrink-0 px-3 text-sm"
        >
          {busy ? "Looking…" : "Look up"}
        </button>
      </div>

      {error && <p className="mt-1.5 text-xs text-(--color-status-high)">{error}</p>}

      {resolved && (
        <p className="mt-1.5 text-xs text-(--color-status-ok)">
          {resolved.label} — save to use it.
        </p>
      )}

      {/* The warning that would have saved this whole round trip. Shown only
          when there is genuinely nothing stored, so it never nags a pool that
          is already set up correctly. */}
      {!resolved && !hasCoordinates && (
        <p className="mt-1.5 text-xs text-(--color-status-high)">
          Not set. The overnight chlorine check and sundown timing are switched off until it is.
        </p>
      )}
    </div>
  );
}
